import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from './Icons';
import { TMDB_LANGUAGES, getLanguageByCode } from '../../lib/languages';
import { useLanguage } from '../../lib/LanguageContext';

export default function LanguageSelector() {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const current = getLanguageByCode(language) || TMDB_LANGUAGES[0];

  const filtered = TMDB_LANGUAGES.filter(
    (l) =>
      l.name.toLowerCase().includes(search.toLowerCase()) ||
      l.native.toLowerCase().includes(search.toLowerCase()) ||
      l.code.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
      >
        <span className="text-xs">{current.code.split('-')[0].toUpperCase()}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-surface-900 border border-surface-800 rounded-xl shadow-2xl overflow-hidden animate-slide-up z-[100]">
          <div className="p-2 border-b border-surface-800">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search languages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-sm text-white placeholder-surface-500 focus:outline-none focus:border-primary/50"
            />
          </div>
          <div className="max-h-80 overflow-y-auto scrollbar-thin">
            {filtered.map((l) => (
              <button
                key={l.code}
                onClick={() => {
                  setLanguage(l.code);
                  setOpen(false);
                  setSearch('');
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  l.code === language
                    ? 'bg-primary/10 text-primary'
                    : 'text-surface-300 hover:text-white hover:bg-surface-800'
                }`}
              >
                <span className="text-xs font-mono text-surface-500 w-10">{l.code.split('-')[0].toUpperCase()}</span>
                <span className="font-medium">{l.name}</span>
                <span className="text-xs text-surface-500 ml-auto">{l.native}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-4 py-6 text-sm text-surface-500 text-center">No languages found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
